export type CodeLanguage = "python" | "javascript" | "csharp" | "curl" | "java" | "go";

export const CODE_LANGUAGES: CodeLanguage[] = ["python", "javascript", "csharp", "curl", "java", "go"];

export type SnippetKind = "webhook" | "api-push";

export type SnippetParams = {
    endpoint: string;
    apiKey: string | null;
    integrationType: string;
    kind: SnippetKind;
};

const API_KEY_PLACEHOLDER = "<YOUR_API_KEY>";

const resolveKey = (apiKey: string | null) => apiKey ?? API_KEY_PLACEHOLDER;

const sourceFieldName = (kind: SnippetKind) => (kind === "webhook" ? "source_id" : "source");

const LANGUAGE_LABELS = {
    python: "Python",
    javascript: "JavaScript",
    csharp: "C#",
    curl: "curl",
    java: "Java",
    go: "Go",
} as const;

export type LanguageLabel = (typeof LANGUAGE_LABELS)[CodeLanguage];

export const languageLabel = (language: CodeLanguage): LanguageLabel => LANGUAGE_LABELS[language];

const MONACO_LANGUAGE_IDS: Record<CodeLanguage, string> = {
    python: "python",
    javascript: "javascript",
    csharp: "csharp",
    curl: "shell",
    java: "java",
    go: "go",
};

export const monacoLanguageFor = (language: CodeLanguage) => MONACO_LANGUAGE_IDS[language];

const pythonSnippet = ({ endpoint, apiKey, integrationType, kind }: SnippetParams) => {
    const key = resolveKey(apiKey);
    const source = sourceFieldName(kind);
    const headers = kind === "webhook"
        ? `headers={\n        "apikey": "${key}",\n        "x-gundi-integration-type": "${integrationType}",\n    }`
        : `headers={"apikey": "${key}"}`;
    return `import requests

requests.post(
    "${endpoint}",
    ${headers},
    json={
        "${source}": "device-001",
        "recorded_at": "2024-01-15T10:30:00+00:00",
        "location": {"lat": -1.2921, "lon": 36.8219},
    },
)
`;
};

const javascriptSnippet = ({ endpoint, apiKey, integrationType, kind }: SnippetParams) => {
    const key = resolveKey(apiKey);
    const source = sourceFieldName(kind);
    const headerLines = [
        `            "apikey": "${key}",`,
        ...(kind === "webhook" ? [`            "x-gundi-integration-type": "${integrationType}",`] : []),
        `            "Content-Type": "application/json",`,
    ].join("\n");
    return `async function sendObservation() {
    await fetch("${endpoint}", {
        method: "POST",
        headers: {
${headerLines}
        },
        body: JSON.stringify({
            ${source}: "device-001",
            recorded_at: "2024-01-15T10:30:00+00:00",
            location: { lat: -1.2921, lon: 36.8219 },
        }),
    });
}

sendObservation();
`;
};

const csharpSnippet = ({ endpoint, apiKey, integrationType, kind }: SnippetParams) => {
    const key = resolveKey(apiKey);
    const source = sourceFieldName(kind);
    const extraHeader = kind === "webhook"
        ? `client.DefaultRequestHeaders.Add("x-gundi-integration-type", "${integrationType}");\n`
        : "";
    return `using System.Net.Http;
using System.Text;
using System.Text.Json;

using var client = new HttpClient();
client.DefaultRequestHeaders.Add("apikey", "${key}");
${extraHeader}
var payload = new
{
    ${source} = "device-001",
    recorded_at = "2024-01-15T10:30:00+00:00",
    location = new { lat = -1.2921, lon = 36.8219 },
};

var content = new StringContent(
    JsonSerializer.Serialize(payload),
    Encoding.UTF8,
    "application/json"
);

await client.PostAsync("${endpoint}", content);
`;
};

const curlSnippet = ({ endpoint, apiKey, integrationType, kind }: SnippetParams) => {
    const key = resolveKey(apiKey);
    const source = sourceFieldName(kind);
    const headerLines = [
        `  -H "apikey: ${key}" \\`,
        ...(kind === "webhook" ? [`  -H "x-gundi-integration-type: ${integrationType}" \\`] : []),
        `  -H "Content-Type: application/json" \\`,
    ].join("\n");
    return `curl -X POST "${endpoint}" \\
${headerLines}
  -d '{"${source}": "device-001", "recorded_at": "2024-01-15T10:30:00+00:00", "location": {"lat": -1.2921, "lon": 36.8219}}'
`;
};

const javaSnippet = ({ endpoint, apiKey, integrationType, kind }: SnippetParams) => {
    const key = resolveKey(apiKey);
    const source = sourceFieldName(kind);
    const extraHeader = kind === "webhook"
        ? `    .header("x-gundi-integration-type", "${integrationType}")\n`
        : "";
    return `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;

var client = HttpClient.newHttpClient();
var body = """
    {
      "${source}": "device-001",
      "recorded_at": "2024-01-15T10:30:00+00:00",
      "location": {"lat": -1.2921, "lon": 36.8219}
    }
    """;

var request = HttpRequest.newBuilder()
    .uri(URI.create("${endpoint}"))
    .header("apikey", "${key}")
${extraHeader}    .header("Content-Type", "application/json")
    .POST(HttpRequest.BodyPublishers.ofString(body))
    .build();

client.send(request, HttpResponse.BodyHandlers.ofString());
`;
};

const goSnippet = ({ endpoint, apiKey, integrationType, kind }: SnippetParams) => {
    const key = resolveKey(apiKey);
    const source = sourceFieldName(kind);
    const extraHeader = kind === "webhook"
        ? `    req.Header.Set("x-gundi-integration-type", "${integrationType}")\n`
        : "";
    return `package main

import (
    "bytes"
    "net/http"
)

func main() {
    payload := []byte(\`{"${source}": "device-001", "recorded_at": "2024-01-15T10:30:00+00:00", "location": {"lat": -1.2921, "lon": 36.8219}}\`)

    req, _ := http.NewRequest("POST", "${endpoint}", bytes.NewBuffer(payload))
    req.Header.Set("apikey", "${key}")
${extraHeader}    req.Header.Set("Content-Type", "application/json")

    client := &http.Client{}
    client.Do(req)
}
`;
};

export const SNIPPET_GENERATORS: Record<CodeLanguage, (params: SnippetParams) => string> = {
    python: pythonSnippet,
    javascript: javascriptSnippet,
    csharp: csharpSnippet,
    curl: curlSnippet,
    java: javaSnippet,
    go: goSnippet,
};

export const generateSnippet = (language: CodeLanguage, params: SnippetParams) =>
    SNIPPET_GENERATORS[language](params);
